import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  Mail,
  FileText,
  BookOpen,
  User,
  Target,
  CheckCircle,
  X,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Calendar,
  Clock,
  DollarSign,
  Users,
  TrendingUp,
  AlertCircle,
  GraduationCap
} from 'lucide-react';
import adminApiService from '../../services/adminApiService';

const StudyPipelineModule = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [showSOPModal, setShowSOPModal] = useState(false);
  const [showPersonalModal, setShowPersonalModal] = useState(false);
  const [showAcademicModal, setShowAcademicModal] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [studentDocuments, setStudentDocuments] = useState([]);
  const [studentSOP, setStudentSOP] = useState(null);
  const [studentPersonalDetails, setStudentPersonalDetails] = useState({});
  const [studentAcademicHistory, setStudentAcademicHistory] = useState({});
  const [studentProgrammePreferences, setStudentProgrammePreferences] = useState({});
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loadingSOP, setLoadingSOP] = useState(false);
  const [filters, setFilters] = useState({
    pipelineStatus: 'all',
    paymentStatus: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0
  });
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadStudents();
    loadStats();
  }, [filters, pagination.page, pagination.limit]);

  const loadStudents = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.studyPipeline.getStudents({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm
      });
      
      // Calculate progress for each student
      const studentsWithProgress = (response.students || []).map(student => {
        const sections = student.sectionsCompleted || {};
        const totalSections = 5;
        const completed = Object.values(sections).filter(Boolean).length;
        const progressPercentage = Math.round((completed / totalSections) * 100);
        
        return {
          ...student,
          progressPercentage,
          getProgressPercentage: () => progressPercentage
        };
      });
      
      setStudents(studentsWithProgress);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        pages: response.pagination?.pages || 0
      }));
    } catch (error) {
      console.error('Error loading students:', error);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await adminApiService.studyPipeline.getStats();
      setStats(response);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleStudentStatusUpdate = async (studentId, updates) => {
    try {
      await adminApiService.studyPipeline.updateStudentStatus(studentId, updates);
      loadStudents();
      loadStats();
    } catch (error) {
      console.error('Error updating student status:', error);
    }
  };

  const handleDocumentStatusUpdate = async (documentId, updates) => {
    try {
      await adminApiService.studyPipeline.updateDocumentStatus(documentId, updates);
      if (selectedStudent) {
        loadStudentDetails(selectedStudent._id);
      }
    } catch (error) {
      console.error('Error updating document status:', error);
    }
  };

  const handleSOPStatusUpdate = async (sopId, updates) => {
    try {
      await adminApiService.studyPipeline.updateSOPStatus(sopId, updates);
      if (selectedStudent) {
        loadStudentSOP(selectedStudent._id);
      }
    } catch (error) {
      console.error('Error updating SOP status:', error);
    }
  };

  const handleAddAdminNote = async (studentId, note) => {
    try {
      await adminApiService.studyPipeline.addAdminNote(studentId, note);
      if (selectedStudent) {
        loadStudentDetails(studentId);
      }
    } catch (error) {
      console.error('Error adding admin note:', error);
    }
  };

  const handleExportCSV = async () => {
    try {
      const blob = await adminApiService.studyPipeline.exportToCSV(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `study_students_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };

  const loadStudentDetails = async (studentId) => {
    try {
      const response = await adminApiService.studyPipeline.getStudent(studentId);
      setSelectedStudent(response);
    } catch (error) {
      console.error('Error loading student details:', error);
    }
  };

  const loadStudentDocuments = async (studentId) => {
    try {
      setLoadingDocuments(true);
      const documents = await adminApiService.studyPipeline.getStudentDocuments(studentId);
      setStudentDocuments(documents || []);
    } catch (error) {
      console.error('Error loading student documents:', error);
      setStudentDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const loadStudentSOP = async (studentId) => {
    try {
      setLoadingSOP(true);
      const sop = await adminApiService.studyPipeline.getStudentSOP(studentId);
      setStudentSOP(sop);
    } catch (error) {
      console.error('Error loading student SOP:', error);
      setStudentSOP(null);
    } finally {
      setLoadingSOP(false);
    }
  };

  const loadStudentPersonalDetails = async (studentId) => {
    try {
      const details = await adminApiService.studyPipeline.getStudentPersonalDetails(studentId);
      setStudentPersonalDetails(details);
    } catch (error) {
      console.error('Error loading student personal details:', error);
      setStudentPersonalDetails({});
    }
  };

  const loadStudentAcademicHistory = async (studentId) => {
    try {
      const history = await adminApiService.studyPipeline.getStudentAcademicHistory(studentId);
      setStudentAcademicHistory(history);
    } catch (error) {
      console.error('Error loading student academic history:', error);
      setStudentAcademicHistory({});
    }
  };

  const loadStudentProgrammePreferences = async (studentId) => {
    try {
      const preferences = await adminApiService.studyPipeline.getStudentProgrammePreferences(studentId);
      setStudentProgrammePreferences(preferences);
    } catch (error) {
      console.error('Error loading student programme preferences:', error);
      setStudentProgrammePreferences({});
    }
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(student => student._id));
    }
  };

  const handleOpenDocumentsModal = async (studentId) => {
    await loadStudentDetails(studentId);
    await loadStudentDocuments(studentId);
    setShowDocumentsModal(true);
  };

  const handleOpenSOPModal = async (studentId) => {
    await loadStudentDetails(studentId);
    await loadStudentSOP(studentId);
    setShowSOPModal(true);
  };

  const handleOpenPersonalModal = async (studentId) => {
    await loadStudentDetails(studentId);
    await loadStudentPersonalDetails(studentId);
    setShowPersonalModal(true);
  };

  const handleOpenAcademicModal = async (studentId) => {
    await loadStudentDetails(studentId);
    await loadStudentAcademicHistory(studentId);
    setShowAcademicModal(true);
  };

  const handleOpenPreferencesModal = async (studentId) => {
    await loadStudentDetails(studentId);
    await loadStudentProgrammePreferences(studentId);
    setShowPreferencesModal(true);
  };

  const getPipelineStatusColor = (status) => {
    const colors = {
      'consultation_scheduled': 'bg-blue-100 text-blue-800',
      'consultation_completed': 'bg-green-100 text-green-800',
      'onboarding': 'bg-yellow-100 text-yellow-800',
      'payment_pending': 'bg-orange-100 text-orange-800',
      'documents_pending': 'bg-purple-100 text-purple-800',
      'sop_pending': 'bg-pink-100 text-pink-800',
      'applications_in_progress': 'bg-indigo-100 text-indigo-800',
      'awaiting_offers': 'bg-cyan-100 text-cyan-800',
      'offer_received': 'bg-emerald-100 text-emerald-800',
      'enrolled': 'bg-teal-100 text-teal-800',
      'completed': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      'unpaid': 'bg-red-100 text-red-800',
      'paid': 'bg-green-100 text-green-800',
      'refunded': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatPipelineStatus = (status) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatPaymentStatus = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#202356]">Study Pipeline</h2>
            <p className="text-gray-600 mt-1">Manage study abroad applications and student progress</p>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Students</p>
                <p className="text-2xl font-bold text-[#202356]">{stats.totalStudents || 0}</p>
              </div>
              <Users className="text-blue-600" size={24} />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Paid Students</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats.paymentStats?.find(s => s._id === 'paid')?.count || 0}
                </p>
              </div>
              <DollarSign className="text-green-600" size={24} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Applications Progress</p>
                <p className="text-2xl font-bold text-purple-600">
                  {stats.pipelineStats?.find(s => s._id === 'applications_in_progress')?.count || 0}
                </p>
              </div>
              <TrendingUp className="text-purple-600" size={24} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Offers Received</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {stats.pipelineStats?.find(s => s._id === 'offer_received')?.count || 0}
                </p>
              </div>
              <CheckCircle className="text-emerald-600" size={24} />
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Filter size={16} />
              Filters
              <ChevronDown size={16} className={`transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>

            <div className="flex items-center gap-3">
              <select
                value={pagination.limit}
                onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
              >
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
              
              <button
                onClick={handleExportCSV}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download size={16} />
                Export CSV
              </button>
              
              <button
                onClick={loadStudents}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw size={16} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline Status</label>
              <select
                value={filters.pipelineStatus}
                onChange={(e) => setFilters(prev => ({ ...prev, pipelineStatus: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
              >
                <option value="all">All Status</option>
                <option value="consultation_scheduled">Consultation Scheduled</option>
                <option value="consultation_completed">Consultation Completed</option>
                <option value="onboarding">Onboarding</option>
                <option value="payment_pending">Payment Pending</option>
                <option value="documents_pending">Documents Pending</option>
                <option value="sop_pending">SOP Pending</option>
                <option value="applications_in_progress">Applications In Progress</option>
                <option value="awaiting_offers">Awaiting Offers</option>
                <option value="offer_received">Offer Received</option>
                <option value="enrolled">Enrolled</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
              <select
                value={filters.paymentStatus}
                onChange={(e) => setFilters(prev => ({ ...prev, paymentStatus: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202356] focus:border-[#202356]"
              >
                <option value="all">All Payment Status</option>
                <option value="unpaid">Unpaid</option>
                <option value="paid">Paid</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          </motion.div>
        )}
      </div>

      {/* Students Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left">
                  <button
                    onClick={handleSelectAll}
                    className="flex items-center justify-center w-5 h-5 border border-gray-300 rounded hover:bg-gray-100"
                  >
                    {selectedStudents.length === students.length && students.length > 0 ? (
                      <CheckCircle size={16} className="text-blue-600" />
                    ) : (
                      <div className="w-4 h-4" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pipeline Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Activity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {students.map((student, index) => (
                <motion.tr
                  key={student._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleSelectStudent(student._id)}
                      className="flex items-center justify-center w-5 h-5 border border-gray-300 rounded hover:bg-gray-100"
                    >
                      {selectedStudents.includes(student._id) ? (
                        <CheckCircle size={16} className="text-blue-600" />
                      ) : (
                        <div className="w-4 h-4" />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">
                        {student.userId?.firstName} {student.userId?.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{student.userId?.email}</div>
                      <div className="text-xs text-gray-400">
                        {student.personalDetails?.phoneNumber || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPipelineStatusColor(student.pipelineStatus)}`}>
                      {formatPipelineStatus(student.pipelineStatus)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPaymentStatusColor(student.paymentStatus)}`}>
                      {formatPaymentStatus(student.paymentStatus)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${student.getProgressPercentage?.() || 0}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600">
                        {student.getProgressPercentage?.() || 0}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500">
                      {new Date(student.lastActivityAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          loadStudentDetails(student._id);
                          setShowDetailsModal(true);
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => handleOpenPersonalModal(student._id)}
                        className="p-1 text-gray-400 hover:text-gray-600 z-50 relative"
                        title="View Personal Details"
                        style={{ zIndex: 50, position: 'relative' }}
                      >
                        <User size={16} />
                      </button>
                      <button
                        onClick={() => handleOpenAcademicModal(student._id)}
                        className="p-1 text-gray-400 hover:text-gray-600 z-50 relative"
                        title="View Academic History"
                        style={{ zIndex: 50, position: 'relative' }}
                      >
                        <BookOpen size={16} />
                      </button>
                      <button
                        onClick={() => handleOpenPreferencesModal(student._id)}
                        className="p-1 text-gray-400 hover:text-gray-600 z-50 relative"
                        title="View Programme Preferences"
                        style={{ zIndex: 50, position: 'relative' }}
                      >
                        <Target size={16} />
                      </button>
                      <button
                        onClick={() => handleOpenDocumentsModal(student._id)}
                        className="p-1 text-gray-400 hover:text-gray-600 z-50 relative"
                        title="View Documents"
                        style={{ zIndex: 50, position: 'relative' }}
                      >
                        <FileText size={16} />
                      </button>
                      <button
                        onClick={() => handleOpenSOPModal(student._id)}
                        className="p-1 text-gray-400 hover:text-gray-600 z-50 relative"
                        title="View SOP"
                        style={{ zIndex: 50, position: 'relative' }}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => window.open(`mailto:${student.userId?.email}`, '_blank')}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Send Email"
                      >
                        <Mail size={16} />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {students.length === 0 && !loading && (
          <div className="text-center py-8">
            <AlertCircle className="text-gray-400 mx-auto mb-2" size={48} />
            <p className="text-gray-500">No students found matching your criteria</p>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Previous
              </button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  const pageNum = i + 1;
                  const isActive = pageNum === pagination.page;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                      className={`px-3 py-1 rounded-md ${isActive ? 'bg-blue-600 text-white' : 'border border-gray-300 hover:bg-gray-100'}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.pages, prev.page + 1) }))}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Student Details Modal */}
      {showDetailsModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">Student Details</h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Student Information */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Student Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-medium">
                      {selectedStudent.userId?.firstName} {selectedStudent.userId?.lastName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium">{selectedStudent.userId?.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-medium">{selectedStudent.personalDetails?.phoneNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Nationality</p>
                    <p className="font-medium">{selectedStudent.personalDetails?.nationality || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Country of Residence</p>
                    <p className="font-medium">{selectedStudent.personalDetails?.countryOfResidence || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Pipeline Status</p>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPipelineStatusColor(selectedStudent.pipelineStatus)}`}>
                      {formatPipelineStatus(selectedStudent.pipelineStatus)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Payment Status</p>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPaymentStatusColor(selectedStudent.paymentStatus)}`}>
                      {formatPaymentStatus(selectedStudent.paymentStatus)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Progress</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${selectedStudent.getProgressPercentage?.() || 0}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium">
                        {selectedStudent.getProgressPercentage?.() || 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Programme Preferences */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Programme Preferences</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Level of Study</p>
                    <p className="font-medium">{selectedStudent.programmePreferences?.levelOfStudy || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Field of Study</p>
                    <p className="font-medium">{selectedStudent.programmePreferences?.fieldOfStudy || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Preferred Countries</p>
                    <p className="font-medium">
                      {(selectedStudent.programmePreferences?.preferredCountries || []).join(', ') || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Budget Range</p>
                    <p className="font-medium">{selectedStudent.programmePreferences?.budgetRange || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Admin Notes */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Admin Notes</h4>
                <div className="space-y-2">
                  {selectedStudent.adminNotes?.length > 0 ? (
                    selectedStudent.adminNotes.map((note, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-900">
                            {note.addedBy?.firstName} {note.addedBy?.lastName}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(note.addedAt).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{note.note}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500">No admin notes yet</p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setShowDocumentsModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <FileText size={16} />
                  View Documents
                </button>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setShowSOPModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <BookOpen size={16} />
                  View SOP
                </button>
                <button
                  onClick={() => window.open(`mailto:${selectedStudent.userId?.email}`, '_blank')}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Mail size={16} />
                  Send Email
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Documents Modal */}
      {showDocumentsModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  Documents - {selectedStudent.userId?.firstName} {selectedStudent.userId?.lastName}
                </h3>
                <button
                  onClick={() => setShowDocumentsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {loadingDocuments ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#202356] mx-auto mb-2"></div>
                  <p className="text-gray-500">Loading documents...</p>
                </div>
              ) : studentDocuments.length > 0 ? (
                <div className="space-y-4">
                  {studentDocuments.map((document) => (
                    <div key={document._id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-gray-900">{document.documentName}</h4>
                          <p className="text-sm text-gray-500">{document.documentType}</p>
                          <p className="text-xs text-gray-400">
                            Uploaded: {new Date(document.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          document.status === 'approved' ? 'bg-green-100 text-green-800' :
                          document.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          document.status === 'under_review' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {document.status.replace('_', ' ')}
                        </span>
                      </div>
                      
                      {document.fileUrl && (
                        <div className="flex items-center gap-2 mt-2">
                          <a
                            href={document.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            View Document
                          </a>
                          <span className="text-gray-400">•</span>
                          <span className="text-sm text-gray-500">
                            {document.fileSize ? `${(document.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                          </span>
                        </div>
                      )}
                      
                      {document.adminNotes && (
                        <div className="mt-2 p-2 bg-gray-50 rounded">
                          <p className="text-sm text-gray-700">{document.adminNotes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="text-gray-400 mx-auto mb-2" size={48} />
                  <p className="text-gray-500">No documents uploaded yet</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* SOP Modal */}
      {showSOPModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  Statement of Purpose - {selectedStudent.userId?.firstName} {selectedStudent.userId?.lastName}
                </h3>
                <button
                  onClick={() => setShowSOPModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {loadingSOP ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#202356] mx-auto mb-2"></div>
                  <p className="text-gray-500">Loading SOP...</p>
                </div>
              ) : studentSOP ? (
                <div className="space-y-6">
                  {/* SOP Status */}
                  <div className="flex items-center justify-between">
                    <div>
                      <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                        studentSOP.status === 'approved' ? 'bg-green-100 text-green-800' :
                        studentSOP.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        studentSOP.status === 'under_review' ? 'bg-yellow-100 text-yellow-800' :
                        studentSOP.status === 'needs_revision' ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {studentSOP.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500">
                      Mode: {studentSOP.mode || 'Not selected'}
                    </div>
                  </div>

                  {/* Guided Form Data */}
                  {studentSOP.mode === 'guided' && studentSOP.guidedFormData && (
                    <div className="space-y-4">
                      <h4 className="text-lg font-medium text-gray-900">Guided Form Responses</h4>
                      
                      {studentSOP.guidedFormData.academicJourney && (
                        <div className="border border-gray-200 rounded-lg p-4">
                          <h5 className="font-medium text-gray-900 mb-2">Academic Journey</h5>
                          <div className="space-y-2 text-sm">
                            {studentSOP.guidedFormData.academicJourney.whyThisField && (
                              <div>
                                <p className="font-medium text-gray-700">Why this field:</p>
                                <p className="text-gray-600">{studentSOP.guidedFormData.academicJourney.whyThisField}</p>
                              </div>
                            )}
                            {studentSOP.guidedFormData.academicJourney.keyAchievements?.length > 0 && (
                              <div>
                                <p className="font-medium text-gray-700">Key achievements:</p>
                                <ul className="list-disc list-inside text-gray-600">
                                  {studentSOP.guidedFormData.academicJourney.keyAchievements.map((achievement, index) => (
                                    <li key={index}>{achievement}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {studentSOP.guidedFormData.careerGoals && (
                        <div className="border border-gray-200 rounded-lg p-4">
                          <h5 className="font-medium text-gray-900 mb-2">Career Goals</h5>
                          <div className="space-y-2 text-sm">
                            {studentSOP.guidedFormData.careerGoals.shortTermGoals && (
                              <div>
                                <p className="font-medium text-gray-700">Short-term goals:</p>
                                <p className="text-gray-600">{studentSOP.guidedFormData.careerGoals.shortTermGoals}</p>
                              </div>
                            )}
                            {studentSOP.guidedFormData.careerGoals.longTermVision && (
                              <div>
                                <p className="font-medium text-gray-700">Long-term vision:</p>
                                <p className="text-gray-600">{studentSOP.guidedFormData.careerGoals.longTermVision}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Uploaded SOP */}
                  {studentSOP.mode === 'upload' && studentSOP.fileUrl && (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">Uploaded Statement of Purpose</h4>
                      <a
                        href={studentSOP.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View SOP Document
                      </a>
                    </div>
                  )}

                  {/* Admin Feedback */}
                  {studentSOP.adminFeedback && (
                    <div className="border border-gray-200 rounded-lg p-4 bg-yellow-50">
                      <h4 className="font-medium text-gray-900 mb-2">Admin Feedback</h4>
                      <p className="text-gray-700">{studentSOP.adminFeedback}</p>
                    </div>
                  )}

                  {/* Admin Notes */}
                  {studentSOP.adminNotes?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Admin Notes</h4>
                      {studentSOP.adminNotes.map((note, index) => (
                        <div key={index} className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-900">
                              {note.addedBy?.firstName} {note.addedBy?.lastName}
                            </span>
                            <span className="text-xs text-gray-500">
                              {new Date(note.addedAt).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700">{note.note}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <BookOpen className="text-gray-400 mx-auto mb-2" size={48} />
                  <p className="text-gray-500">No SOP submitted yet</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Personal Details Modal */}
      {showPersonalModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  Personal Details - {selectedStudent.userId?.firstName} {selectedStudent.userId?.lastName}
                </h3>
                <button
                  onClick={() => setShowPersonalModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {studentPersonalDetails && Object.keys(studentPersonalDetails).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">Basic Information</h4>
                    
                    <div>
                      <label className="text-sm text-gray-500">Date of Birth</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.dateOfBirth 
                          ? new Date(studentPersonalDetails.dateOfBirth).toLocaleDateString() 
                          : 'Not provided'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">Gender</label>
                      <p className="text-gray-900 capitalize">
                        {studentPersonalDetails.gender || 'Not provided'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">Marital Status</label>
                      <p className="text-gray-900 capitalize">
                        {studentPersonalDetails.maritalStatus || 'Not provided'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">Nationality</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.nationality || 'Not provided'}
                      </p>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">Contact Information</h4>
                    
                    <div>
                      <label className="text-sm text-gray-500">Country of Residence</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.countryOfResidence || 'Not provided'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">City</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.city || 'Not provided'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">Address</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.address || 'Not provided'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">Postal Code</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.postalCode || 'Not provided'}
                      </p>
                    </div>
                  </div>

                  {/* Phone Information */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">Phone Information</h4>
                    
                    <div>
                      <label className="text-sm text-gray-500">Primary Phone</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.phoneNumber || 'Not provided'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">Alternate Phone</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.alternatePhone || 'Not provided'}
                      </p>
                    </div>
                  </div>

                  {/* Passport Information */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">Passport Information</h4>
                    
                    <div>
                      <label className="text-sm text-gray-500">Passport Number</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.passportNumber || 'Not provided'}
                      </p>
                    </div>

                    <div>
                      <label className="text-sm text-gray-500">Passport Expiry</label>
                      <p className="text-gray-900">
                        {studentPersonalDetails.passportExpiryDate 
                          ? new Date(studentPersonalDetails.passportExpiryDate).toLocaleDateString() 
                          : 'Not provided'}
                      </p>
                    </div>
                  </div>

                  {/* Emergency Contact */}
                  <div className="space-y-4 md:col-span-2">
                    <h4 className="font-medium text-gray-900 border-b pb-2">Emergency Contact</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm text-gray-500">Contact Name</label>
                        <p className="text-gray-900">
                          {studentPersonalDetails.emergencyContactName || 'Not provided'}
                        </p>
                      </div>

                      <div>
                        <label className="text-sm text-gray-500">Contact Phone</label>
                        <p className="text-gray-900">
                          {studentPersonalDetails.emergencyContactPhone || 'Not provided'}
                        </p>
                      </div>

                      <div>
                        <label className="text-sm text-gray-500">Relationship</label>
                        <p className="text-gray-900">
                          {studentPersonalDetails.emergencyContactRelationship || 'Not provided'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <User className="text-gray-400 mx-auto mb-2" size={48} />
                  <p className="text-gray-500">No personal details provided yet</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Academic History Modal */}
      {showAcademicModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  Academic History - {selectedStudent.userId?.firstName} {selectedStudent.userId?.lastName}
                </h3>
                <button
                  onClick={() => setShowAcademicModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {studentAcademicHistory && Object.keys(studentAcademicHistory).length > 0 ? (
                <div className="space-y-6">
                  {/* Highest Qualification */}
                  <div>
                    <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Highest Qualification</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-900 capitalize">
                        {studentAcademicHistory.highestQualification || 'Not provided'}
                      </p>
                    </div>
                  </div>

                  {/* Institutions */}
                  {studentAcademicHistory.institutions && studentAcademicHistory.institutions.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Educational Institutions</h4>
                      <div className="space-y-4">
                        {studentAcademicHistory.institutions.map((institution, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="text-sm text-gray-500">Institution Name</label>
                                <p className="text-gray-900 font-medium">{institution.name || 'Not provided'}</p>
                              </div>
                              <div>
                                <label className="text-sm text-gray-500">Country</label>
                                <p className="text-gray-900">{institution.country || 'Not provided'}</p>
                              </div>
                              <div>
                                <label className="text-sm text-gray-500">Field of Study</label>
                                <p className="text-gray-900">{institution.fieldOfStudy || 'Not provided'}</p>
                              </div>
                              <div>
                                <label className="text-sm text-gray-500">Qualification Awarded</label>
                                <p className="text-gray-900">{institution.qualificationAwarded || 'Not provided'}</p>
                              </div>
                              <div>
                                <label className="text-sm text-gray-500">Start Date</label>
                                <p className="text-gray-900">
                                  {institution.startDate 
                                    ? new Date(institution.startDate).toLocaleDateString() 
                                    : 'Not provided'}
                                </p>
                              </div>
                              <div>
                                <label className="text-sm text-gray-500">End Date</label>
                                <p className="text-gray-900">
                                  {institution.endDate 
                                    ? new Date(institution.endDate).toLocaleDateString() 
                                    : 'Not provided'}
                                </p>
                              </div>
                              <div>
                                <label className="text-sm text-gray-500">Grade</label>
                                <p className="text-gray-900">{institution.grade || 'Not provided'}</p>
                              </div>
                              <div>
                                <label className="text-sm text-gray-500">Grade System</label>
                                <p className="text-gray-900">{institution.gradeSystem || 'Not provided'}</p>
                              </div>
                            </div>
                            {institution.isCurrentlyStudying && (
                              <div className="mt-3">
                                <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                  Currently Studying
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Academic Performance */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Academic Strengths</h4>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <p className="text-gray-900">
                          {studentAcademicHistory.academicStrengths || 'Not provided'}
                        </p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Academic Challenges</h4>
                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <p className="text-gray-900">
                          {studentAcademicHistory.academicChallenges || 'Not provided'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Academic Gaps */}
                  <div>
                    <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Academic Gaps</h4>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          studentAcademicHistory.hasAcademicGaps 
                            ? 'bg-orange-100 text-orange-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {studentAcademicHistory.hasAcademicGaps ? 'Has Academic Gaps' : 'No Academic Gaps'}
                        </span>
                      </div>
                      {studentAcademicHistory.hasAcademicGaps && studentAcademicHistory.academicGapExplanation && (
                        <div className="bg-orange-50 p-4 rounded-lg">
                          <label className="text-sm text-gray-500">Explanation</label>
                          <p className="text-gray-900 mt-1">
                            {studentAcademicHistory.academicGapExplanation}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* English Proficiency */}
                  <div>
                    <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">English Proficiency</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="text-sm text-gray-500">Test Type</label>
                        <p className="text-gray-900 capitalize">
                          {studentAcademicHistory.englishProficiency?.testType || 'Not provided'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-500">Score</label>
                        <p className="text-gray-900">
                          {studentAcademicHistory.englishProficiency?.score || 'Not provided'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-500">Test Date</label>
                        <p className="text-gray-900">
                          {studentAcademicHistory.englishProficiency?.testDate 
                            ? new Date(studentAcademicHistory.englishProficiency.testDate).toLocaleDateString() 
                            : 'Not provided'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-500">Plan to Take Test</label>
                        <p className="text-gray-900">
                          {studentAcademicHistory.englishProficiency?.planToTakeTest ? 'Yes' : 'No'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <BookOpen className="text-gray-400 mx-auto mb-2" size={48} />
                  <p className="text-gray-500">No academic history provided yet</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Programme Preferences Modal */}
      {showPreferencesModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  Programme Preferences - {selectedStudent.userId?.firstName} {selectedStudent.userId?.lastName}
                </h3>
                <button
                  onClick={() => setShowPreferencesModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {studentProgrammePreferences && Object.keys(studentProgrammePreferences).length > 0 ? (
                <div className="space-y-6">
                  {/* Study Preferences */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Study Preferences</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm text-gray-500">Level of Study</label>
                          <p className="text-gray-900 capitalize">
                            {studentProgrammePreferences.levelOfStudy?.replace('_', ' ') || 'Not provided'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Field of Study</label>
                          <p className="text-gray-900">
                            {studentProgrammePreferences.fieldOfStudy || 'Not provided'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Study Mode</label>
                          <p className="text-gray-900 capitalize">
                            {studentProgrammePreferences.studyMode?.replace('_', ' ') || 'Not provided'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Intake Preference</label>
                          <p className="text-gray-900 capitalize">
                            {studentProgrammePreferences.intakePreference || 'Not provided'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Financial & Support</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm text-gray-500">Budget Range</label>
                          <p className="text-gray-900">
                            {studentProgrammePreferences.budgetRange || 'Not provided'}
                          </p>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div>
                            <label className="text-sm text-gray-500">Scholarship Interest</label>
                            <p className="text-gray-900">
                              {studentProgrammePreferences.scholarshipInterest ? 'Yes' : 'No'}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm text-gray-500">Accommodation Needed</label>
                            <p className="text-gray-900">
                              {studentProgrammePreferences.accommodationNeeded ? 'Yes' : 'No'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Geographic Preferences */}
                  <div>
                    <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Geographic Preferences</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm text-gray-500">Preferred Countries</label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {studentProgrammePreferences.preferredCountries?.length > 0 ? (
                            studentProgrammePreferences.preferredCountries.map((country, index) => (
                              <span key={index} className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                {country}
                              </span>
                            ))
                          ) : (
                            <p className="text-gray-900">Not provided</p>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="text-sm text-gray-500">Preferred Cities</label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {studentProgrammePreferences.preferredCities?.length > 0 ? (
                            studentProgrammePreferences.preferredCities.map((city, index) => (
                              <span key={index} className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                {city}
                              </span>
                            ))
                          ) : (
                            <p className="text-gray-900">Not provided</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Programme Choices */}
                  {studentProgrammePreferences.programmeChoices && studentProgrammePreferences.programmeChoices.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Programme Choices</h4>
                      <div className="space-y-3">
                        {studentProgrammePreferences.programmeChoices.map((choice, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="text-sm text-gray-500">Institution</label>
                                <p className="text-gray-900 font-medium">{choice.institution || 'Not provided'}</p>
                              </div>
                              <div>
                                <label className="text-sm text-gray-500">Programme</label>
                                <p className="text-gray-900">{choice.programme || 'Not provided'}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Specific Programmes */}
                  {studentProgrammePreferences.specificProgrammes && studentProgrammePreferences.specificProgrammes.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Specific Programmes</h4>
                      <div className="flex flex-wrap gap-2">
                        {studentProgrammePreferences.specificProgrammes.map((programme, index) => (
                          <span key={index} className="inline-flex px-3 py-1 text-sm font-medium bg-purple-100 text-purple-800 rounded-full">
                            {programme}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Additional Preferences */}
                  {studentProgrammePreferences.additionalPreferences && (
                    <div>
                      <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">Additional Preferences</h4>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-900">
                          {studentProgrammePreferences.additionalPreferences}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Target className="text-gray-400 mx-auto mb-2" size={48} />
                  <p className="text-gray-500">No programme preferences provided yet</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default StudyPipelineModule;
